import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { userHasPermission } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";
import { toast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  FolderOpen,
  Loader2,
  Upload,
  XCircle,
  Download,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Folder = {
  id: string;
  name: string;
  kind: "inbox" | "archive" | "custom";
  isPartnerFolder?: boolean;
};

type ClientFile = {
  id: string;
  relativePath: string;
  fileName: string;
  sizeBytes: number;
  contentType: string;
  createdAt: string;
  folderId: string;
};

type UploadItemStatus = "queued" | "uploading" | "done" | "error" | "cancelled";

type UploadItem = {
  id: string;
  file: File;
  relativePath: string;
  status: UploadItemStatus;
  progress: number;
  error?: string;
  fileId?: string;
};

const CONCURRENCY = 5;
const URL_CHUNK = 50;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<
  Array<{ file: File; relativePath: string }>
> {
  const items = Array.from(dataTransfer.items || []);
  if (items.length && typeof items[0].webkitGetAsEntry === "function") {
    const entries = items
      .map((item) => item.webkitGetAsEntry())
      .filter(Boolean) as FileSystemEntry[];

    const collected: Array<{ file: File; relativePath: string }> = [];

    async function walk(entry: FileSystemEntry, prefix: string) {
      if (entry.isFile) {
        const fileEntry = entry as FileSystemFileEntry;
        const file = await new Promise<File>((resolve, reject) =>
          fileEntry.file(resolve, reject),
        );
        collected.push({
          file,
          relativePath: prefix ? `${prefix}/${file.name}` : file.name,
        });
        return;
      }

      if (entry.isDirectory) {
        const dirEntry = entry as FileSystemDirectoryEntry;
        const reader = dirEntry.createReader();
        const readBatch = (): Promise<FileSystemEntry[]> =>
          new Promise((resolve, reject) => {
            reader.readEntries(resolve, reject);
          });

        let batch = await readBatch();
        while (batch.length) {
          for (const child of batch) {
            await walk(
              child,
              prefix ? `${prefix}/${entry.name}` : entry.name,
            );
          }
          batch = await readBatch();
        }
      }
    }

    for (const entry of entries) {
      await walk(entry, "");
    }
    return collected;
  }

  return Array.from(dataTransfer.files).map((file) => ({
    file,
    relativePath: (file as File & { webkitRelativePath?: string })
      .webkitRelativePath || file.name,
  }));
}

export default function ClientFilesPage() {
  const { user } = useAuth();
  const isPartnerUser = Boolean(user?.partnerId);
  const canUpload = userHasPermission(
    user?.effectivePermissions,
    "portal.files.upload",
  );

  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const cancelRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const { data: folders = [], isLoading: foldersLoading } = useQuery<Folder[]>({
    queryKey: ["/api/portal/files/folders"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const uploadFolders = useMemo(
    () =>
      folders.filter(
        (f) => f.kind !== "archive" && (isPartnerUser || !f.isPartnerFolder),
      ),
    [folders, isPartnerUser],
  );

  const activeFolderId =
    selectedFolderId ||
    uploadFolders.find((f) => f.kind === "inbox")?.id ||
    uploadFolders[0]?.id ||
    "";

  const { data: files = [], isLoading: filesLoading } = useQuery<ClientFile[]>({
    queryKey: ["/api/portal/files/files", activeFolderId],
    queryFn: async () => {
      if (!activeFolderId) return [];
      const res = await fetch(
        `/api/portal/files/files?folderId=${encodeURIComponent(activeFolderId)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: !!activeFolderId,
  });

  const queueFiles = useCallback(
    (incoming: Array<{ file: File; relativePath: string }>) => {
      const next: UploadItem[] = incoming.map((item) => ({
        id: crypto.randomUUID(),
        file: item.file,
        relativePath: item.relativePath.replace(/^\/+/, ""),
        status: "queued",
        progress: 0,
      }));
      setUploads((current) => [...current, ...next]);
    },
    [],
  );

  const updateUpload = useCallback(
    (id: string, patch: Partial<UploadItem>) => {
      setUploads((current) =>
        current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  const overallProgress = useMemo(() => {
    if (!uploads.length) return 0;
    const total = uploads.reduce((sum, item) => sum + item.progress, 0);
    return Math.round(total / uploads.length);
  }, [uploads]);

  const startUpload = async () => {
    if (!canUpload || !activeFolderId) return;
    const pending = uploads.filter((u) => u.status === "queued" || u.status === "error");
    if (!pending.length) {
      toast({ title: "Nothing to upload", description: "Add files first." });
      return;
    }

    cancelRef.current = false;
    setIsUploading(true);

    try {
      const batchRes = await apiRequest("POST", "/api/portal/files/batches", {
        folderId: activeFolderId,
        expectedCount: pending.length,
      });
      const batch = await batchRes.json();

      for (let i = 0; i < pending.length; i += URL_CHUNK) {
        if (cancelRef.current) break;
        const chunk = pending.slice(i, i + URL_CHUNK);

        const urlsRes = await apiRequest("POST", "/api/portal/files/upload-urls", {
          batchId: batch.id,
          files: chunk.map((item) => ({
            relativePath: item.relativePath,
            contentType: item.file.type || "application/octet-stream",
            sizeBytes: item.file.size,
          })),
        });
        const signed = (await urlsRes.json()) as Array<{
          fileId: string;
          relativePath: string;
          uploadUrl: string;
          requiredHeaders?: Record<string, string>;
        }>;

        const byPath = new Map(signed.map((s) => [s.relativePath, s]));

        let index = 0;
        const workers = Array.from({ length: Math.min(CONCURRENCY, chunk.length) }, async () => {
          while (index < chunk.length) {
            if (cancelRef.current) return;
            const currentIndex = index++;
            const item = chunk[currentIndex];
            const signedItem = byPath.get(item.relativePath);
            if (!signedItem) {
              updateUpload(item.id, {
                status: "error",
                error: "Missing upload URL",
                progress: 0,
              });
              continue;
            }

            updateUpload(item.id, {
              status: "uploading",
              fileId: signedItem.fileId,
              progress: 5,
              error: undefined,
            });

            try {
              await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", signedItem.uploadUrl);
                const contentType =
                  signedItem.requiredHeaders?.["Content-Type"] ||
                  item.file.type ||
                  "application/octet-stream";
                xhr.setRequestHeader("Content-Type", contentType);
                xhr.upload.onprogress = (event) => {
                  if (!event.lengthComputable) return;
                  const pct = Math.max(
                    5,
                    Math.min(95, Math.round((event.loaded / event.total) * 100)),
                  );
                  updateUpload(item.id, { progress: pct });
                };
                xhr.onload = () => {
                  if (xhr.status >= 200 && xhr.status < 300) resolve();
                  else reject(new Error(`Upload failed (${xhr.status})`));
                };
                xhr.onerror = () =>
                  reject(
                    new Error(
                      "Network/CORS error talking to storage. Ensure R2 bucket CORS allows this origin and Content-Type.",
                    ),
                  );
                xhr.send(item.file);
              });

              await apiRequest("POST", "/api/portal/files/complete", {
                fileId: signedItem.fileId,
              });
              updateUpload(item.id, { status: "done", progress: 100 });
            } catch (error) {
              const message =
                error instanceof Error ? error.message : "Upload failed";
              try {
                await apiRequest("POST", "/api/portal/files/complete", {
                  fileId: signedItem.fileId,
                  failed: true,
                  errorMessage: message,
                });
              } catch {
                // ignore complete failure marker errors
              }
              updateUpload(item.id, {
                status: "error",
                error: message,
                progress: 0,
              });
            }
          }
        });

        await Promise.all(workers);
      }

      await queryClient.invalidateQueries({
        queryKey: ["/api/portal/files/files", activeFolderId],
      });

      toast({
        title: cancelRef.current ? "Upload cancelled" : "Upload finished",
        description: cancelRef.current
          ? "Remaining queued files were left unsent."
          : "Your files are available in this folder.",
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const downloadMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const res = await apiRequest(
        "GET",
        `/api/portal/files/files/${fileId}/download-url`,
      );
      return res.json() as Promise<{ downloadUrl: string }>;
    },
    onSuccess: (data) => {
      window.open(data.downloadUrl, "_blank", "noopener,noreferrer");
    },
    onError: (error: Error) => {
      toast({
        title: "Download failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Files"
        description={
          isPartnerUser
            ? `Upload files${user?.partnerName ? ` to ${user.partnerName}` : ""}. You can confirm what you sent here, but you cannot download files or see other folders.`
            : "Upload assets for our team. Drop whole folders when you need to send project packs."
        }
      />

      {!isPartnerUser && (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-xs">
          <Select
            value={activeFolderId}
            onValueChange={setSelectedFolderId}
            disabled={foldersLoading || !folders.length}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select folder" />
            </SelectTrigger>
            <SelectContent>
              {folders.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>
                  {folder.name}
                  {folder.kind === "archive" ? " (read-only)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      )}

      {canUpload &&
        folders.find((f) => f.id === activeFolderId)?.kind !== "archive" &&
        (isPartnerUser ||
          !folders.find((f) => f.id === activeFolderId)?.isPartnerFolder) && (
        <Card
          className={cn(
            "border-dashed",
            dragOver && "border-primary bg-primary/5",
          )}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={async (event) => {
            event.preventDefault();
            setDragOver(false);
            const collected = await collectDroppedFiles(event.dataTransfer);
            queueFiles(collected);
          }}
        >
          <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
            <div className="rounded-full bg-primary/10 p-3">
              <Upload className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-medium">Drop files or folders here</p>
              <p className="text-sm text-muted-foreground">
                Concurrent uploads with live progress. Large assets supported.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                Choose files
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => folderInputRef.current?.click()}
                disabled={isUploading}
              >
                Choose folder
              </Button>
              <Button
                type="button"
                onClick={startUpload}
                disabled={isUploading || uploads.every((u) => u.status === "done")}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  "Start upload"
                )}
              </Button>
              {isUploading && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    cancelRef.current = true;
                  }}
                >
                  Cancel remaining
                </Button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                const files = Array.from(event.target.files || []).map((file) => ({
                  file,
                  relativePath: file.name,
                }));
                queueFiles(files);
                event.target.value = "";
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              multiple
              className="hidden"
              // @ts-expect-error webkitdirectory is non-standard but widely supported
              webkitdirectory=""
              onChange={(event) => {
                const files = Array.from(event.target.files || []).map((file) => ({
                  file,
                  relativePath:
                    (file as File & { webkitRelativePath?: string })
                      .webkitRelativePath || file.name,
                }));
                queueFiles(files);
                event.target.value = "";
              }}
            />
          </CardContent>
        </Card>
      )}

      {uploads.length > 0 && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">Upload queue</p>
                <p className="text-sm text-muted-foreground">
                  {uploads.filter((u) => u.status === "done").length}/
                  {uploads.length} complete
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isUploading}
                onClick={() => setUploads([])}
              >
                Clear
              </Button>
            </div>
            <Progress value={overallProgress} />
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {uploads.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{item.relativePath}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(item.file.size)}
                      {item.error ? ` · ${item.error}` : ""}
                    </p>
                    {(item.status === "uploading" || item.status === "queued") && (
                      <Progress value={item.progress} className="mt-2 h-1.5" />
                    )}
                  </div>
                  <div className="shrink-0 pt-0.5">
                    {item.status === "done" && (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    )}
                    {item.status === "error" && (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                    {item.status === "uploading" && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-medium">Files in folder</h2>
          </div>
          {filesLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !files.length ? (
            <p className="text-sm text-muted-foreground">No files yet.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {file.relativePath}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(file.sizeBytes)} · {formatDateTime(file.createdAt)}
                    </p>
                  </div>
                  {!isPartnerUser && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => downloadMutation.mutate(file.id)}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download
                  </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
